import { FC, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ThemeProvider, useTheme } from 'styled-components';
import { darkColors, Modal, StyledButton } from '@tryhackme/thm-ui-components';
import { usePricing } from 'src/common/hooks';
import { useCampaignStorage } from 'src/features/campaigns/hooks/use-campaign-storage';
import { useGetUserQuery, useTrackEventMutation } from 'src/common/slices';
import { useActiveCampaign } from '../../hooks/use-active-campaign';
import { useCampaignCta, useMonthlyPlanHikedPrice } from '../beat-the-price-hike.hooks';
import { Countdown } from '../../components/countdown/countdown';
import { Benefits } from './components/benefits';
import { PriceHike } from './components/price-hike';
import { StyledContent, StyledCenteredContent, StyledHeading, StyledSubTitle, StyledButtons, TopGradient, BottomGradient } from './beat-the-price-hike-modal.styles';
import { DiscountMessage } from './components/discount-message';
export const BeatTheHikePriceModal: FC = () => {
  const theme = useTheme();
  const userQuery = useGetUserQuery();
  const yearlyPricing = usePricing(true);
  const monthlyHikedPricing = useMonthlyPlanHikedPrice();
  const isPremium = userQuery.data?.data?.user?.isPremium;
  const [trackEvent] = useTrackEventMutation();
  const {
    campaign
  } = useActiveCampaign();
  const {
    isSeen,
    markAsSeen
  } = useCampaignStorage(campaign);
  const [ctaText, urlToNavigate] = useCampaignCta(campaign);
  const [open, setOpen] = useState(Boolean(campaign && !isSeen));
  const symbol = yearlyPricing.symbol ?? '$';
  const yearlyPrice = Number.parseFloat(yearlyPricing.price);
  const discountedYearlyPrice = yearlyPrice * (100 - (campaign?.amount ?? 0)) / 100;
  useEffect(() => {
    if (isSeen) {
      setOpen(false);
    }
  }, [isSeen]);
  const onOpenChange = (isOpen: boolean) => {
    if (isOpen) return;
    markAsSeen();
    setOpen(false);
  };
  const onCtaClick = () => {
    markAsSeen();
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    trackEvent({
      event: 'click-subscribe-now',
      properties: {
        'clicked-on': 'dashboard-pop-up'
      }
    });
  };
  return campaign ? <ThemeProvider theme={{
    ...theme,
    colors: darkColors
  }}>
      <Modal padding="0" open={open} onOpenChange={onOpenChange} preventCloseOnOutsideClick>
        <StyledContent>
          <TopGradient />
          <BottomGradient />
          <StyledCenteredContent>
            <StyledHeading data-testid="campaign-title">SAVE BIG BEFORE PRICES GO UP!</StyledHeading>
            <StyledSubTitle data-testid="campaign-promotion-text">
              <span>{campaign.amount}% off</span> annual subscriptions
            </StyledSubTitle>
          </StyledCenteredContent>
          <Countdown includeDays vertical dark startTime={campaign.startTime} endTime={campaign.endTime} />
          {isPremium && monthlyHikedPricing ? <PriceHike {...monthlyHikedPricing} /> : <Benefits />}
          <DiscountMessage isPremium={Boolean(isPremium)} yearlyPrice={yearlyPrice} discountedYearlyPrice={discountedYearlyPrice} symbol={symbol} />
          <StyledButtons>
            <StyledButton as={Link} role="button" to={urlToNavigate} aria-label={ctaText} variant="solid" color="primary" onClick={onCtaClick}>
              {ctaText}
            </StyledButton>
          </StyledButtons>
        </StyledContent>
      </Modal>
    </ThemeProvider> : null;
};